import React from "react";
import { CalendarClock, CheckCircle2, FileText, Lock } from "lucide-react";
import { format, parseISO } from "date-fns";
import InputLine from "~/components/RegisterForm/InputLine";
import { BalanceBox, BalanceLine } from "../../components/BalanceLines";
import { monthLabel, accountLabel } from "../monthOptions";

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

export default function MonthPanel({ monthData, onOpenStatement, onCloseMonth }) {
  const isClosed = monthData?.status === "Closed";
  const canClose = !!monthData?.can_close;
  const monthComplete = !!monthData?.month_complete;
  const pending = Number(monthData?.pending_items || 0);
  const review = Number(monthData?.review_items || 0);
  const open = pending + review;

  return (
    <>
      <InputLine title="Information">
        <div className="flex flex-row flex-wrap items-center gap-2 text-xs text-gray-600 pt-2">
          <strong>{accountLabel(monthData?.bankAccount)}</strong>
          <span>·</span>
          <strong>{monthLabel(monthData?.month, monthData?.year)}</strong>
          <span>·</span>
          <span>
            {safeDate(monthData?.date_from)} to {safeDate(monthData?.date_to)}
          </span>
          <span>·</span>
          <span className={isClosed ? "text-emerald-600" : "text-gray-500"}>
            {monthData?.status}
          </span>
        </div>
      </InputLine>

      <InputLine title="Balance">
        <div className="w-full pt-2">
          <BalanceBox>
            <BalanceLine
              title="Beginning balance"
              value={monthData?.beginning_balance}
            />
            <BalanceLine title="Credits (IN)" value={monthData?.credits} />
            <BalanceLine title="Debits (OUT)" value={monthData?.debits} />
            <BalanceLine
              title="Net movement"
              value={monthData?.net_movement}
              tone={
                Number(monthData?.net_movement || 0) >= 0 ? "success" : "danger"
              }
            />
            <BalanceLine
              title="Ending balance"
              value={monthData?.ending_balance}
            />
            <div className="w-full flex flex-row items-center justify-between text-gray-500 pt-2">
              <span>
                {monthData?.verified_items ?? 0}/{monthData?.total_items ?? 0}{" "}
                verified
              </span>
              <div className="flex flex-row items-center gap-2">
                {review > 0 ? (
                  <span className="text-orange-600 font-bold">
                    {review} to review
                  </span>
                ) : null}
                <span>{pending} without match</span>
              </div>
            </div>
            <div className="w-full flex flex-row items-center justify-between text-gray-500">
              <span>Covered until {safeDate(monthData?.last_imported_date)}</span>
              <span>{monthData?.total_statements ?? 0} statements</span>
            </div>
          </BalanceBox>
        </div>
      </InputLine>

      {!isClosed && !monthComplete && monthData?.next_expected_start ? (
        <InputLine>
          <div className="w-full flex flex-row items-center gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-700 rounded-md p-2">
            <CalendarClock size={16} />
            Next weekday to cover: {" "}
            <strong>{safeDate(monthData.next_expected_start)}</strong>
          </div>
        </InputLine>
      ) : null}

      {!isClosed && monthComplete ? (
        <InputLine>
          <div className="w-full flex flex-row items-center gap-2 text-xs border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-md p-2">
            <CheckCircle2 size={16} />
            The month is fully covered.
            {open > 0
              ? ` ${open} movements still open.`
              : " All movements reconciled."}
          </div>
        </InputLine>
      ) : null}

      <InputLine title="Statements">
        <table className="w-full text-xs">
          <thead className="border-b">
            <tr className="bg-secondary h-8">
              <th>Period</th>
              <th>Movements</th>
              <th>Pending</th>
              <th className="w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="align-center">
            {monthData?.statements?.length ? (
              monthData.statements.map((statement) => (
                <tr key={statement.id} className="border-b">
                  <td className="py-1">
                    <div className="flex flex-row items-center justify-center">
                      {safeDate(statement.date_from)} to{" "}
                      {safeDate(statement.date_to)}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      {statement.total_items ?? 0}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      {statement.pending_items ?? 0}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row items-center justify-center">
                      <button
                        type="button"
                        title="Open movements"
                        onClick={() => onOpenStatement(statement)}
                        className="hover:scale-125 transition-all"
                      >
                        <FileText size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center text-gray-500 py-8">
                  No statements imported yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </InputLine>

      {!isClosed ? (
        <InputLine title="Actions">
          <div className="flex flex-col items-start gap-2 pt-2">
            <button
              type="button"
              disabled={!canClose}
              onClick={onCloseMonth}
              className="bg-primary text-white rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:opacity-80 disabled:opacity-40 transition-all"
            >
              <Lock size={16} /> Close month
            </button>
            {!canClose ? (
              <span className="text-xs text-gray-500">
                {!monthComplete
                  ? "The month is not fully covered by statements yet."
                  : open > 0
                  ? `${open} movements still open — ${review} to review, ${pending} without match.`
                  : "This month cannot be closed yet."}
              </span>
            ) : null}
          </div>
        </InputLine>
      ) : null}
    </>
  );
}
